import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Avatar,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Report,
  CheckCircle,
  Cancel,
  Warning,
  Visibility,
  Person,
  ShoppingBag,
  OpenInNew,
  ArrowBack,
  ArrowForward,
  Block,
  Delete,
} from '@mui/icons-material';
import { useGetReportsQuery, useResolveReportMutation } from '../../store/api/adminApi';

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [resolveDialog, setResolveDialog] = useState(false);
  const [resolutionAction, setResolutionAction] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [resolutionReason, setResolutionReason] = useState('');
  const [banUser, setBanUser] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { data: reportsData, isLoading, error, refetch } = useGetReportsQuery({
    page: page + 1,
    limit: rowsPerPage,
    status: statusFilter,
  } as any);

  const [resolveReport] = useResolveReportMutation();

  const reports = (reportsData as any)?.reports || [];
  const totalReports = (reportsData as any)?.total || 0;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResolveReport = async () => {
    if (!selectedReport) return;

    const requestData: any = {
      id: selectedReport.id,
      action: resolutionAction,
      reason: resolutionReason
    };
    
    // ユーザー通報の場合、BANオプションを追加
    if (selectedReport.targetType === 'user' && resolutionAction === 'APPROVED' && banUser) {
      requestData.banUser = true;
    }
    
    console.log('通報処理リクエストデータ:', requestData);

    try {
      await resolveReport(requestData).unwrap();
      
      setResolveDialog(false);
      setSelectedReport(null);
      setResolutionReason('');
      setBanUser(false);
      refetch();
    } catch (error) {
      console.error('Report resolution failed:', error);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Chip label="未対応" color="warning" size="small" icon={<Warning />} />;
      case 'RESOLVED':
        return <Chip label="対応済み" color="success" size="small" icon={<CheckCircle />} />;
      case 'REJECTED':
        return <Chip label="却下" color="error" size="small" icon={<Cancel />} />;
      default:
        return <Chip label="不明" color="default" size="small" />;
    }
  };

  const getReportTypeText = (type: string) => {
    switch (type) {
      case 'INAPPROPRIATE_CONTENT':
        return '不適切なコンテンツ';
      case 'SPAM':
        return 'スパム';
      case 'HARASSMENT':
        return 'ハラスメント';
      case 'FAKE_PRODUCT':
        return '偽物商品';
      case 'COPYRIGHT_VIOLATION':
        return '著作権侵害';
      case 'OTHER':
        return 'その他';
      default:
        return type || 'その他';
    }
  };

  const getTargetTypeText = (targetType: string) => {
    switch (targetType) {
      case 'product':
        return '商品';
      case 'user':
        return 'ユーザー';
      case 'message':
        return 'メッセージ';
      default:
        return targetType || '不明';
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY'
    }).format(price);
  };

  const handleImageNavigation = (direction: 'prev' | 'next') => {
    if (!selectedReport?.targetDetails?.images) return;
    const totalImages = selectedReport.targetDetails.images.length;
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  const openReportDetail = (report: any) => {
    setSelectedReport(report);
    setResolutionAction('APPROVED');
    setResolutionReason('');
    setBanUser(false);
    setCurrentImageIndex(0);
    setResolveDialog(true);
  };

  const navigateToTarget = (targetType: string, targetId: string) => {
    if (targetType === 'product') {
      navigate(`/products?search=${targetId}`);
    } else if (targetType === 'user') {
      navigate(`/users?search=${targetId}`);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        通報データの読み込みに失敗しました
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        通報・問い合わせ管理
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        ユーザーからの通報を確認し、適切に対応してください。
        <br />
        <strong>承認</strong>すると、商品の場合は該当商品が削除されます。ユーザーの場合は利用停止（BAN）を選択できます。
      </Alert>

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>ステータス</InputLabel>
          <Select
            value={statusFilter}
            label="ステータス"
            onChange={(e) => setStatusFilter(e.target.value as string)}
          >
            <MenuItem value="PENDING">未対応</MenuItem>
            <MenuItem value="RESOLVED">対応済み</MenuItem>
            <MenuItem value="REJECTED">却下</MenuItem>
            <MenuItem value="all">全て</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>通報対象</TableCell>
              <TableCell>通報種類</TableCell>
              <TableCell>通報者</TableCell>
              <TableCell>理由</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>通報日</TableCell>
              <TableCell>アクション</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report: any) => (
              <TableRow key={report.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {report.targetType === 'product' ? (
                      <>
                        {report.targetDetails?.images?.[0] ? (
                          <Card sx={{ width: 50, height: 50, flexShrink: 0 }}>
                            <CardMedia
                              component="img"
                              height="50"
                              image={report.targetDetails.images[0]}
                              alt={report.targetDetails?.title || '商品画像'}
                            />
                          </Card>
                        ) : (
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            <ShoppingBag />
                          </Avatar>
                        )}
                        <Box>
                          <Chip 
                            label="商品" 
                            size="small" 
                            color="primary" 
                            icon={<ShoppingBag />}
                            sx={{ mb: 0.5 }}
                          />
                          <Typography variant="body2" fontWeight="bold" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {report.targetDetails?.title || report.targetName || report.targetId}
                          </Typography>
                          {report.targetDetails?.price && (
                            <Typography variant="caption" color="textSecondary">
                              {formatPrice(report.targetDetails.price)}
                            </Typography>
                          )}
                        </Box>
                      </>
                    ) : (
                      <>
                        <Avatar 
                          src={report.targetDetails?.profileImage}
                          sx={{ bgcolor: 'secondary.light' }}
                        >
                          <Person />
                        </Avatar>
                        <Box>
                          <Chip 
                            label="ユーザー" 
                            size="small" 
                            color="secondary" 
                            icon={<Person />}
                            sx={{ mb: 0.5 }}
                          />
                          <Typography variant="body2" fontWeight="bold">
                            {report.targetDetails?.name || report.targetName || report.targetId}
                          </Typography>
                          {report.targetDetails?.email && (
                            <Typography variant="caption" color="textSecondary">
                              {report.targetDetails.email}
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getReportTypeText(report.type || report.reason || 'OTHER')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {report.reporterDetails?.name || report.reporterName || report.reporterId || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {report.description || report.reason || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>{getStatusChip(report.status || 'PENDING')}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(report.createdAt).toLocaleDateString('ja-JP')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => openReportDetail(report)}
                    >
                      詳細
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalReports}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* 通報詳細・処理ダイアログ */}
      <Dialog
        open={resolveDialog}
        onClose={() => setResolveDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Report sx={{ mr: 1 }} />
            通報詳細・処理
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box>
              {/* 通報対象の詳細カード */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" gutterBottom>
                      {selectedReport.targetType === 'product' ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <ShoppingBag color="primary" />
                          通報対象：商品
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person color="secondary" />
                          通報対象：ユーザー
                        </Box>
                      )}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<OpenInNew />}
                      onClick={() => navigateToTarget(selectedReport.targetType, selectedReport.targetId)}
                    >
                      {selectedReport.targetType === 'product' ? '商品管理で確認' : 'ユーザー管理で確認'}
                    </Button>
                  </Box>

                  {/* 商品の場合 */}
                  {selectedReport.targetType === 'product' && selectedReport.targetDetails && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {/* 商品画像 */}
                      {selectedReport.targetDetails.images && selectedReport.targetDetails.images.length > 0 && (
                        <Box sx={{ width: { xs: '100%', md: '30%' }, flexShrink: 0 }}>
                          <Card>
                            <CardMedia
                              component="img"
                              height="200"
                              image={selectedReport.targetDetails.images[currentImageIndex]}
                              alt="商品画像"
                              sx={{ objectFit: 'cover' }}
                            />
                          </Card>
                          {selectedReport.targetDetails.images.length > 1 && (
                            <Box display="flex" justifyContent="center" alignItems="center" mt={1} gap={1}>
                              <IconButton size="small" onClick={() => handleImageNavigation('prev')}>
                                <ArrowBack />
                              </IconButton>
                              <Typography variant="caption">
                                {currentImageIndex + 1} / {selectedReport.targetDetails.images.length}
                              </Typography>
                              <IconButton size="small" onClick={() => handleImageNavigation('next')}>
                                <ArrowForward />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {/* 商品情報 */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {selectedReport.targetDetails.title}
                        </Typography>
                        <Typography variant="h6" color="primary" gutterBottom>
                          {formatPrice(selectedReport.targetDetails.price || 0)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                          {selectedReport.targetDetails.description || '説明なし'}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2">
                          <strong>商品ID:</strong> {selectedReport.targetId}
                        </Typography>
                        <Typography variant="body2">
                          <strong>ステータス:</strong> {selectedReport.targetDetails.status}
                        </Typography>
                        <Typography variant="body2">
                          <strong>出品者:</strong> {selectedReport.targetDetails.sellerName || 'Unknown'}
                          {selectedReport.targetDetails.sellerEmail && ` (${selectedReport.targetDetails.sellerEmail})`}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* ユーザーの場合 */}
                  {selectedReport.targetType === 'user' && selectedReport.targetDetails && (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={selectedReport.targetDetails.profileImage}
                        sx={{ width: 80, height: 80 }}
                      >
                        <Person sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {selectedReport.targetDetails.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {selectedReport.targetDetails.email}
                        </Typography>
                        <Typography variant="body2">
                          <strong>ユーザーID:</strong> {selectedReport.targetId}
                        </Typography>
                        <Box display="flex" gap={1} mt={1}>
                          {selectedReport.targetDetails.isVerified && (
                            <Chip label="本人確認済み" size="small" color="success" />
                          )}
                          {selectedReport.targetDetails.isBanned && (
                            <Chip label="利用停止中" size="small" color="error" icon={<Block />} />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* 対象情報がない場合 */}
                  {!selectedReport.targetDetails && (
                    <Alert severity="warning">
                      対象の詳細情報を取得できませんでした。削除済みの可能性があります。
                      <br />
                      対象ID: {selectedReport.targetId}
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* 通報詳細 */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    通報内容
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>通報ID:</strong> {selectedReport.id}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>通報種類:</strong> {getReportTypeText(selectedReport.type || selectedReport.reason || 'OTHER')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>通報者:</strong> {selectedReport.reporterDetails?.name || selectedReport.reporterName || selectedReport.reporterId || 'N/A'}
                        {selectedReport.reporterDetails?.email && ` (${selectedReport.reporterDetails.email})`}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>通報日時:</strong> {new Date(selectedReport.createdAt).toLocaleString('ja-JP')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>ステータス:</strong> {getStatusChip(selectedReport.status)}
                      </Typography>
                    </Box>
                  </Box>
                  {(selectedReport.description || selectedReport.reason) && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        通報理由・詳細：
                      </Typography>
                      <Typography variant="body2">
                        {selectedReport.description || selectedReport.reason}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* 対応処理フォーム */}
              {selectedReport.status === 'PENDING' && (
                <Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    対応処理
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>処理結果</InputLabel>
                    <Select
                      value={resolutionAction}
                      label="処理結果"
                      onChange={(e) => {
                        setResolutionAction(e.target.value as 'APPROVED' | 'REJECTED');
                        if (e.target.value === 'REJECTED') {
                          setBanUser(false);
                        }
                      }}
                    >
                      <MenuItem value="APPROVED">承認（通報内容が適切）</MenuItem>
                      <MenuItem value="REJECTED">却下（通報内容が不適切）</MenuItem>
                    </Select>
                  </FormControl>

                  {/* 承認時の処理説明 */}
                  {resolutionAction === 'APPROVED' && (
                    <Alert severity="warning" sx={{ mb: 2 }} icon={<Delete />}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        承認すると以下の処理が実行されます：
                      </Typography>
                      {selectedReport.targetType === 'product' ? (
                        <Typography variant="body2">
                          • 通報された商品「{selectedReport.targetDetails?.title || selectedReport.targetId}」が削除されます
                        </Typography>
                      ) : (
                        <Box>
                          <Typography variant="body2">
                            • 通報が承認済みとして記録されます
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={banUser}
                                onChange={(e) => setBanUser(e.target.checked)}
                                color="error"
                              />
                            }
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Block color="error" />
                                <Typography variant="body2" color="error.main" fontWeight="bold">
                                  このユーザーを利用停止（BAN）にする
                                </Typography>
                              </Box>
                            }
                          />
                        </Box>
                      )}
                    </Alert>
                  )}

                  {resolutionAction === 'REJECTED' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      却下すると、通報は無効とみなされ、対象への影響はありません。
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="処理理由・詳細（必須）"
                    value={resolutionReason}
                    onChange={(e) => setResolutionReason(e.target.value)}
                    placeholder="処理の理由や詳細を記入してください"
                    required
                    error={!resolutionReason.trim()}
                    helperText={!resolutionReason.trim() ? '処理理由は必須です' : ''}
                  />
                </Box>
              )}

              {/* 対応済みの場合、処理結果を表示 */}
              {selectedReport.status !== 'PENDING' && (
                <Card sx={{ bgcolor: selectedReport.status === 'RESOLVED' ? 'success.50' : 'error.50' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      処理結果
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>処理結果:</strong> {selectedReport.status === 'RESOLVED' ? '承認済み' : '却下'}
                    </Typography>
                    {selectedReport.resolvedAt && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>処理日時:</strong> {new Date(selectedReport.resolvedAt).toLocaleString('ja-JP')}
                      </Typography>
                    )}
                    {selectedReport.resolution && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          処理理由：
                        </Typography>
                        <Typography variant="body2">
                          {selectedReport.resolution}
                        </Typography>
                      </Box>
                    )}
                    {selectedReport.actionResult && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          実行されたアクション：
                        </Typography>
                        {selectedReport.actionResult.productDeleted && (
                          <Chip label="商品を削除しました" color="error" size="small" icon={<Delete />} />
                        )}
                        {selectedReport.actionResult.userBanned && (
                          <Chip label="ユーザーを利用停止にしました" color="error" size="small" icon={<Block />} sx={{ ml: 1 }} />
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog(false)}>
            閉じる
          </Button>
          {selectedReport?.status === 'PENDING' && (
            <Button
              variant="contained"
              color={resolutionAction === 'APPROVED' ? 'warning' : 'primary'}
              onClick={handleResolveReport}
              disabled={!resolutionReason.trim()}
              startIcon={resolutionAction === 'APPROVED' ? <CheckCircle /> : <Cancel />}
            >
              {resolutionAction === 'APPROVED' ? '承認して処理' : '却下'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsPage;
